import { ThemeProvider } from '@mui/material';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/system';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';
import { DocumentDateInfo, HeaderPublic, Loader, ScrollMenu } from '../../components';
import { PATHS } from '../../config/consts';
import actions, { selectors } from '../../modules/common';
import Content from './Content';
import { getHtmlContent, getSidebarData, getTitleContent } from './helper';

const styles = {
  content: {
    maxWidth: 660,
    pb: 10,
    pt: 4,
  },
  title: {
    fontSize: '2rem',
    fontWeight: 'bold',
    letterSpacing: 0,
    lineHeight: 1.334,
    m: 0,
  },
};

export interface IData {
  dateCreated: string;
  dateEffective: string;
  html: string;
  name: string;
}

export function GTCPageDetail() {
  const dispatch = useDispatch();
  const data: IData = useSelector(selectors.getGTCDetail);
  const isFetching = useSelector(selectors.isFetchingGTCDetail);
  const notFound = useSelector(selectors.isNotFoundGTCDetail);
  const { slug, version = null } = useParams();
  const fullPath = encodeURI(((version ?? '') ? `${slug ?? ''}/${version}` : (slug ?? '')).trim());
  useEffect(() => {
    if (!notFound) {
      dispatch(actions.loadGTCDetail(fullPath));
    }
  }, [fullPath]);

  if (notFound && !isFetching && !data?.html) {
    return <Navigate replace to={PATHS.GTC_PAGES} />;
  }

  const html = data?.html ? getHtmlContent(data.html) : '';
  const documentTitle = data?.name || '';
  const sidebarData = getSidebarData(html, documentTitle);

  return (
    <ThemeProvider
      theme={(globalTheme: Theme) => ({
        ...globalTheme,
        palette: { ...(globalTheme?.palette || {}), background: { default: 'white' } },
      })}
    >
      <CssBaseline />
      <HeaderPublic />
      <Container maxWidth="xl">
        <Grid container>
          <Grid display={{ md: 'block', xs: 'none' }} size={{ xs: 3 }}>
            {sidebarData.length > 1 && <ScrollMenu menuData={sidebarData} />}
          </Grid>
          <Grid size="grow" sx={styles.content}>
            {isFetching ? (
              <Loader />
            ) : (
              <>
                <Box
                  component="h1"
                  dangerouslySetInnerHTML={{ __html: getTitleContent(documentTitle) }}
                  sx={styles.title}
                />
                {data && <DocumentDateInfo effective={data?.dateEffective} posted={data?.dateCreated} />}
                <Content html={html} />
              </>
            )}
          </Grid>
        </Grid>
      </Container>
    </ThemeProvider>
  );
}
